import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { resolveDigitalArtwork, findArtworkById } from "../../services/artworkResolver";
import { useDigitalArtwork } from "../../hooks/useTranslatedArtwork";
import { useArtworkDocumentMeta } from "../../hooks/useArtworkDocumentMeta";
import { ProductsExplorer } from "../Product/ProductsExplorer";
import { ArtworkDetailLayout } from "./ArtworkDetailLayout";
import { artworkPagePath } from "../../lib/artworkPaths";
import { ArtworkExternalLinks } from "../ArtworkExternalLinks";
import { RelatedArtworksSection } from "../RelatedArtworksSection";
import type { ArtWork } from "../../types/ArtWork";

export function DigitalArtworkPage() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const work = resolveDigitalArtwork(id);

  if (!work) {
    return (
      <div className="section-container py-24 text-center">
        <h1 className="font-display text-3xl text-canvas-950">
          {t("common.artworkNotFound")}
        </h1>
        <Link to="/gallery?tab=digital" className="btn-primary mt-6">
          {t("common.backToGallery")}
        </Link>
      </div>
    );
  }

  return <DigitalArtworkPageContent work={work} />;
}

/** Legacy route — redirect to canonical artwork URL for any medium. */
export function LegacyArtworkRedirect() {
  const { id = "" } = useParams();
  const resolved = findArtworkById(id);
  if (resolved) {
    return <Navigate to={artworkPagePath(resolved.medium, id)} replace />;
  }
  return <DigitalArtworkPage />;
}

function DigitalArtworkPageContent({ work }: { work: ArtWork }) {
  const translated = useDigitalArtwork(work);

  useArtworkDocumentMeta({
    title: `${translated.title} | Shanir Arts`,
    description: translated.description,
    imageUrl: translated.imageUrl,
    canonicalPath: artworkPagePath("digital", work.id),
  });

  return (
    <ArtworkDetailLayout
      medium="digital"
      artworkId={work.id}
      artworkName={translated.name}
      eyebrow={translated.collectionLabel}
      title={translated.title}
      description={translated.description}
      imageUrl={translated.imageUrl}
      imageAlt={translated.name}
      links={<ArtworkExternalLinks links={work.links} />}
      footer={
        <>
          <RelatedArtworksSection
            medium="digital"
            currentId={work.id}
            collection={translated.collection}
            collectionLabel={translated.collectionLabel}
          />
          <ProductsExplorer artWork={work} />
        </>
      }
    />
  );
}
